import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {isAxiosError} from 'axios'
import {useLocation, useParams, useSearchParams} from 'react-router-dom'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type SgsstItemStatus = components['schemas']['SgsstItemStatus']
export type SgsstBand = components['schemas']['SgsstBand']
export type SgsstExposure = components['schemas']['ExposureRead']
export type SgsstBackedByKind = components['schemas']['SgsstBackedByKind']

export type SgsstItem = components['schemas']['SgsstItemRead']

// Resuelta por el backend: la pantalla no ordena categorías ni deriva bandas. `null` = la
// empresa no tiene clase de riesgo.
export type DisenoLicencia = components['schemas']['DisenoLicenciaRead']

export type SoporteCasilla = components['schemas']['SoporteEstadoRead']

// `estado` distingue un mes cubierto, un hueco real y un mes cuyo pago aún no vence.
export type PeriodoMensualCelda = components['schemas']['PeriodoMensualCeldaRead']

export type SgsstAssessment = Omit<components['schemas']['SgsstAssessmentRead'], 'items'> & {
  items: SgsstItem[]
  pending_sugerencias_count: number
}

export type SgsstState = Omit<components['schemas']['SgsstStateRead'], 'assessment'> & {
  assessment: SgsstAssessment | null
}

// `year` = undefined shows the current open period; a number shows that year (read-only if closed).
export function useSgsst(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['sgsst', businessId, year ?? 'current'],
    queryFn: async () =>
      (await api.get<SgsstState>(`/businesses/${businessId}/sgsst`, {params: year ? {year} : undefined})).data,
    enabled: !!businessId,
  })
}

export type Expediente = components['schemas']['ExpedienteRead']

export function useExpedientes(businessId: string | undefined) {
  return useQuery({
    queryKey: ['expedientes', businessId],
    queryFn: async () => (await api.get<Expediente[]>(`/businesses/${businessId}/sgsst/expedientes`)).data,
    enabled: !!businessId,
  })
}

export function useSgsstPage() {
  const {businessId} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {hash} = useLocation()
  const viewedYear = searchParams.get('year') ? Number(searchParams.get('year')) : undefined
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const isOwner = business?.role === 'owner'
  // `canFirmar` es la atestación del art. 27, por rol y sin licencia; `canDisenar` exige
  // licencia suficiente para la banda (arts. 4/10/17).
  const capabilities = useCapabilities(businessId)
  const canOperate = capabilities.sgsst_operar
  const canFirmar = capabilities.sgsst_firmar
  const firmaBlockedReason = capabilities.firmar_bloqueado_por
  const canDisenar = capabilities.sgsst_disenar
  const disenoBlockedReason = capabilities.disenar_bloqueado_por
  // Solo en dev: el usuario de sistema pasa ambas compuertas para E2E; los veredictos siguen
  // reportando la licencia faltante. Siempre false en prod.
  const certDevOverride = capabilities.sgsst_dev_override
  const tieneSstVerificado = capabilities.tiene_sst_verificado
  const tieneResponsableDesignado = capabilities.tiene_responsable_designado
  const {data: state, isLoading} = useSgsst(canOperate ? businessId : undefined, viewedYear)
  const godReadOnly = useGodModeReadOnly()
  const periodYears = state?.periods.map((p) => p.year) ?? []

  function selectYear(year: number) {
    if (year === periodYears[0]) setSearchParams({}, {replace: true})
    else setSearchParams({year: String(year)}, {replace: true})
  }

  return {
    businessId,
    hash,
    searchParams,
    viewedYear,
    business,
    isOwner,
    capabilities,
    canOperate,
    canFirmar,
    firmaBlockedReason,
    canDisenar,
    disenoBlockedReason,
    certDevOverride,
    tieneSstVerificado,
    tieneResponsableDesignado,
    state,
    godReadOnly,
    periodYears,
    selectYear,
    loading: businessLoading || isLoading,
  }
}

function useSgsstInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
}

/** Declaración del empleador que reemplaza los reportes de eventos de un estándar 3.2.x. */
export function useDeclararSinEventos(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: {code: string; texto?: string | null}) =>
      api.post(`/businesses/${businessId}/sgsst/items/${input.code}/sin-eventos`, {texto: input.texto ?? null}),
    onSuccess: invalidate,
  })
}

export function useRevocarSinEventos(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: {code: string; declaracionId: string}) =>
      api.delete(`/businesses/${businessId}/sgsst/items/${input.code}/sin-eventos/${input.declaracionId}`),
    onSuccess: invalidate,
  })
}

export function useCloseAssessment(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => api.post(`/businesses/${businessId}/sgsst/assessment/close`, {}),
    onSuccess: () => {
      invalidate()
      // El cierre copia evidencia, designaciones, acciones y solicitudes abiertas al año nuevo
      // y sella un expediente.
      queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
      queryClient.invalidateQueries({queryKey: ['designations', businessId]})
      queryClient.invalidateQueries({queryKey: ['actions', businessId]})
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
      queryClient.invalidateQueries({queryKey: ['expedientes', businessId]})
    },
  })
}

export function useFinishAutoevaluacion(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async () => api.post(`/businesses/${businessId}/sgsst/autoevaluacion`, {}),
    onSuccess: () => {
      invalidate()
      track('autoevaluacion_finished', {business_id: businessId})
    },
  })
}

export function useRegisterArlReport(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async () => api.post(`/businesses/${businessId}/sgsst/arl-reporte`, {}),
    onSuccess: invalidate,
  })
}

export function useCorrectItem(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {code: string; status: SgsstItemStatus; note: string; justification?: string | null}) =>
      api.post(`/businesses/${businessId}/sgsst/items/${input.code}/correccion`, {
        status: input.status,
        note: input.note,
        justification: input.justification ?? null,
      }),
    onSuccess: () => {
      invalidate()
      // a corrección can release or re-arm the plan's resolve band, and a
      // correction to no aplica cancels the standard's open solicitudes
      queryClient.invalidateQueries({queryKey: ['actions', businessId]})
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
    },
  })
}

export function useResolveItem(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => api.post(`/businesses/${businessId}/sgsst/items/${code}/resolucion`, {}),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['actions', businessId]})
    },
  })
}

export function useStartAssessment(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async (workerCount: number) =>
      api.post(`/businesses/${businessId}/sgsst/assessment`, {worker_count: workerCount}),
    onSuccess: invalidate,
  })
}

export function useRederiveAssessment(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  return useMutation({
    mutationFn: async (workerCount: number) =>
      api.patch(`/businesses/${businessId}/sgsst/assessment`, {worker_count: workerCount}),
    onSuccess: invalidate,
  })
}

export function useMarkItem(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      code: string
      status: SgsstItemStatus
      justification?: string | null
      // Required by the API only when this call walks an EXISTING mark back to
      // a different status within the same open period (an in-period correction).
      note?: string | null
    }) =>
      api.put(`/businesses/${businessId}/sgsst/items/${input.code}`, {
        status: input.status,
        justification: input.justification ?? null,
        note: input.note ?? null,
      }),
    onSuccess: (_data, variables) => {
      invalidate()
      // marking a standard no aplica cancels its open solicitudes
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
      track('item_marked', {business_id: businessId, standard_code: variables.code})
    },
  })
}

export function useUnmarkItem(businessId: string) {
  const invalidate = useSgsstInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => api.delete(`/businesses/${businessId}/sgsst/items/${code}`),
    onSuccess: (_data, code) => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
      track('item_unmarked', {business_id: businessId, standard_code: code})
    },
  })
}

export type SgsstAuditExport = components['schemas']['SgsstAuditExportRead']

// El estado es compartido: el sondeo corre aunque el job lo haya lanzado otra persona. 404
// (nunca se pidió) es `null`.
export function useAuditExport(businessId: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: ['sgsst-export', businessId, year],
    queryFn: async () => {
      try {
        return (await api.get<SgsstAuditExport>(`/businesses/${businessId}/sgsst/${year}/export-auditoria`)).data
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null
        throw err
      }
    },
    enabled: !!businessId && year !== undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'pendiente' || status === 'procesando' ? 2000 : false
    },
  })
}

// Sin `regenerar` el inicio es idempotente en el servidor: un doble clic no lanza otro armado.
export function useStartAuditExport(businessId: string, year: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (regenerar?: boolean) =>
      (
        await api.post<SgsstAuditExport>(
          `/businesses/${businessId}/sgsst/${year}/export-auditoria${regenerar ? '?regenerar=true' : ''}`,
          {}
        )
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['sgsst-export', businessId, year], data)
      // Con el despachador en línea el aviso ya existe al resolver el POST, y la campana
      // solo se repregunta cada 60 s.
      if (data.status === 'listo') queryClient.invalidateQueries({queryKey: ['notifications']})
      track('audit_export_started', {business_id: businessId})
    },
  })
}

// Mirrors backend derive_chapter (arts. 2, 8, 14 of Res. 0312/2019) so the setup
// card can preview the chapter live; the authoritative pin happens server-side.
export function previewChapter(workerCount: number, riskClass: number): 1 | 2 | 3 {
  if (riskClass >= 4 || workerCount > 50) return 3
  if (workerCount > 10) return 2
  return 1
}

export const CHAPTER_STANDARD_COUNTS: Record<number, number> = {1: 7, 2: 21, 3: 60}
